import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserState } from '@/types/user'
import { refreshToken as refreshTokenApi } from '@/api/auth'

export const useUserStore = defineStore(
  'user',
  () => {
    // State
    const token = ref<string>('')
    const refreshToken = ref<string>('')
    const userInfo = ref<UserState | null>(null)
    const loading = ref(false)

    // Getters
    const isAuthenticated = computed(() => !!token.value && !!userInfo.value)
    const userId = computed(() => userInfo.value?.userId)
    const username = computed(() => userInfo.value?.realName)
    const userPhone = computed(() => userInfo.value?.phone)
    const avatar = computed(() => userInfo.value?.avatar)
    const isVip = computed(() => userInfo.value?.isVip ?? false)
    const isVipExpired = computed(() => {
      if (!userInfo.value?.vipExpireTime) return false
      return new Date(userInfo.value.vipExpireTime) < new Date()
    })

    // Actions
    const setToken = (newToken: string) => {
      token.value = newToken
    }

    const setRefreshToken = (newRefreshToken: string) => {
      refreshToken.value = newRefreshToken
    }

    const setUserInfo = (info: UserState) => {
      userInfo.value = info
    }

    const refreshAccessToken = async (): Promise<boolean> => {
      if (!refreshToken.value) return false
      try {
        const res = await refreshTokenApi(refreshToken.value)
        token.value = res.data.accessToken
        refreshToken.value = res.data.refreshToken
        if (userInfo.value) {
          userInfo.value.userId = res.data.userId
          userInfo.value.realName = res.data.realName
          userInfo.value.phone = res.data.phone
          if (res.data.avatar) userInfo.value.avatar = res.data.avatar
          userInfo.value.isVip = res.data.isVip
          userInfo.value.vipExpireTime = res.data.vipExpireTime
        }
        return true
      } catch (error) {
        // 刷新失败，清除登录状态
        logout()
        return false
      }
    }

    const updateProfile = (data: Partial<UserState>) => {
      if (userInfo.value) {
        userInfo.value = { ...userInfo.value, ...data }
      }
    }

    const updateAvatar = (newAvatar: string) => {
      if (userInfo.value) {
        userInfo.value.avatar = newAvatar
      }
    }

    const logout = () => {
      token.value = ''
      refreshToken.value = ''
      userInfo.value = null
      // 重置 AI 助手状态，防止新用户看到上一个用户的会话/消息/激活状态
      import('@/stores/ai-assistant').then(m => m.useAIAssistantStore().reset())
    }

    return {
      // State
      token,
      refreshToken,
      userInfo,
      loading,
      // Getters
      isAuthenticated,
      userId,
      username,
      userPhone,
      avatar,
      isVip,
      isVipExpired,
      // Actions
      setToken,
      setRefreshToken,
      setUserInfo,
      refreshAccessToken,
      updateProfile,
      updateAvatar,
      logout,
    }
  },
  {
    persist: {
      pick: ['token', 'refreshToken', 'userInfo'],
    },
  }
)
